import React from 'react';
import { Link } from 'react-router-dom';
import Navbar from '@/components/Navbar';
import { Card } from '@/components/ui/card';

export default function Terms() {
  return (
    <div className="min-h-screen bg-background">
      <Navbar />
      
      <div className="max-w-4xl mx-auto px-6 md:px-12 py-12">
        <h1 className="text-4xl font-bold tracking-tight mb-2">Terms of Service</h1>
        <p className="text-muted-foreground mb-8">Last updated: January 2026</p>

        <Card className="p-8 md:p-12">
          <div className="prose prose-lg max-w-none space-y-8">
            
            <section>
              <h2 className="text-2xl font-bold mb-4">1. Acceptance of Terms</h2>
              <p>By accessing or using EzyTransfer services provided by Bullaale AS ("Company", "we", "us", or "our"), you agree to be bound by these Terms of Service ("Terms"). If you do not agree to these Terms, please do not use our services.</p>
            </section>

            <section>
              <h2 className="text-2xl font-bold mb-4">2. Description of Services</h2>
              <p>EzyTransfer provides digital money transfer and remittance services, including but not limited to:</p>
              <ul className="list-disc pl-6 space-y-2 mt-3">
                <li>International money transfers</li>
                <li>Cryptocurrency payments and conversions</li>
                <li>Wallet services for storing and managing funds</li>
                <li>Mobile money integration</li>
                <li>Banking and payment services through partner integrations</li>
              </ul>
            </section>

            <section>
              <h2 className="text-2xl font-bold mb-4">3. Eligibility</h2>
              <p>To use our services, you must:</p>
              <ul className="list-disc pl-6 space-y-2 mt-3">
                <li>Be at least 18 years of age</li>
                <li>Have the legal capacity to enter into a binding agreement</li>
                <li>Not be prohibited from using our services under applicable laws</li>
                <li>Provide accurate and complete registration information</li>
              </ul>
            </section>

            <section>
              <h2 className="text-2xl font-bold mb-4">4. Account Registration and Security</h2>
              <p>You are responsible for:</p>
              <ul className="list-disc pl-6 space-y-2 mt-3">
                <li>Maintaining the confidentiality of your account credentials</li>
                <li>All activities that occur under your account</li>
                <li>Notifying us immediately of any unauthorized access</li>
                <li>Ensuring your contact information is accurate and up-to-date</li>
              </ul>
            </section>

            <section>
              <h2 className="text-2xl font-bold mb-4">5. Know Your Customer (KYC) and Anti-Money Laundering (AML)</h2>
              <p>In compliance with regulatory requirements, we are required to:</p>
              <ul className="list-disc pl-6 space-y-2 mt-3">
                <li>Verify your identity before providing certain services</li>
                <li>Request documentation to confirm your identity and source of funds</li>
                <li>Monitor transactions for suspicious activity</li>
                <li>Report suspicious activities to relevant authorities as required by law</li>
              </ul>
              <p className="mt-3">You agree to cooperate with all KYC and AML procedures and provide accurate information and documentation as requested.</p>
            </section>

            <section>
              <h2 className="text-2xl font-bold mb-4">6. Fees and Charges</h2>
              <p>Our fee structure includes:</p>
              <ul className="list-disc pl-6 space-y-2 mt-3">
                <li>Transaction fees for money transfers</li>
                <li>Currency conversion fees</li>
                <li>Withdrawal fees for external wallets and bank accounts</li>
                <li>Any applicable third-party fees</li>
              </ul>
              <p className="mt-3">All fees will be disclosed before you confirm a transaction. We reserve the right to modify our fee structure with prior notice.</p>
            </section>

            <section>
              <h2 className="text-2xl font-bold mb-4">7. Prohibited Activities</h2>
              <p>You agree not to use our services for:</p>
              <ul className="list-disc pl-6 space-y-2 mt-3">
                <li>Money laundering or terrorist financing</li>
                <li>Fraud or any illegal activities</li>
                <li>Transactions involving illegal goods or services</li>
                <li>Circumventing any applicable laws or regulations</li>
                <li>Unauthorized access to our systems or other users' accounts</li>
              </ul>
            </section>

            <section>
              <h2 className="text-2xl font-bold mb-4">8. Transaction Limits</h2>
              <p>We may impose limits on:</p>
              <ul className="list-disc pl-6 space-y-2 mt-3">
                <li>The amount you can send or receive</li>
                <li>The frequency of transactions</li>
                <li>Daily, weekly, or monthly transaction volumes</li>
              </ul>
              <p className="mt-3">These limits may vary based on your verification level and jurisdiction.</p>
            </section>

            <section>
              <h2 className="text-2xl font-bold mb-4">9. Liability Limitations</h2>
              <p>To the maximum extent permitted by law:</p>
              <ul className="list-disc pl-6 space-y-2 mt-3">
                <li>We are not liable for any indirect, incidental, or consequential damages</li>
                <li>Our total liability shall not exceed the fees paid by you in the preceding 12 months</li>
                <li>We are not responsible for delays caused by third-party service providers</li>
              </ul>
            </section>

            <section>
              <h2 className="text-2xl font-bold mb-4">10. Termination</h2>
              <p>We reserve the right to suspend or terminate your account if:</p>
              <ul className="list-disc pl-6 space-y-2 mt-3">
                <li>You violate these Terms</li>
                <li>We suspect fraudulent or illegal activity</li>
                <li>Required by law or regulatory authorities</li>
                <li>You fail to complete required verification procedures</li>
              </ul>
            </section>

            <section>
              <h2 className="text-2xl font-bold mb-4">11. Governing Law</h2>
              <p>These Terms shall be governed by and construed in accordance with the laws of Norway. Any disputes arising from these Terms shall be subject to the exclusive jurisdiction of the courts of Norway.</p>
            </section>

            <section>
              <h2 className="text-2xl font-bold mb-4">12. Changes to Terms</h2>
              <p>We may modify these Terms at any time. We will notify you of material changes via email or through our platform. Your continued use of our services after such notification constitutes acceptance of the modified Terms.</p>
            </section>

            <section>
              <h2 className="text-2xl font-bold mb-4">13. Contact Information</h2>
              <p>For questions about these Terms, please contact:</p>
              <p className="mt-3">
                <strong>Bullaale AS</strong><br />
                Email: legal@ezytransfer.com<br />
                Website: www.ezytransfer.com
              </p>
            </section>

          </div>
        </Card>

        {/* Footer Links */}
        <div className="flex flex-wrap justify-center gap-6 text-sm text-muted-foreground mt-8">
          <Link to="/about" className="hover:text-accent">About</Link>
          <Link to="/terms" className="hover:text-accent font-semibold text-accent">Terms of Service</Link>
          <Link to="/privacy" className="hover:text-accent">Privacy Policy</Link>
          <Link to="/cookies" className="hover:text-accent">Cookie Policy</Link>
        </div>
      </div>
    </div>
  );
}
